import { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { LoggedInUser } from '@atlassian/learning-components';

import { getAtlassianUser } from 'services/http/user';
import { User } from 'typings/types';

type UserState = User | null | undefined;

interface UserContextProps {
  user: User | null | undefined;
  userIsInitialized?: boolean;
}

interface Props {
  children: ReactNode;
  initialUser?: User | null;
  initialUserIsInitialized?: boolean;
}

export const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children, initialUser, initialUserIsInitialized = false }: Props) => {
  const [user, setUser] = useState<UserState>(initialUser);
  const [userIsInitialized, setUserIsInitialized] = useState<boolean>(initialUserIsInitialized);

  const memoizedUser = useMemo(() => ({ user, userIsInitialized }), [user, userIsInitialized]);

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      try {
        const userData = await getAtlassianUser();
        setUser(userData);
        setUserIsInitialized(true);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setUserIsInitialized(true); // Mark as initialized even if failed
      }
    };

    // safeguard in case SSR redirect won't work
    if (!user && !userIsInitialized) {
      fetchUser();
    }
  }, [user, userIsInitialized]);

  return <UserContext.Provider value={memoizedUser}> {children}</UserContext.Provider>;
};

export const normalizeUser = (user: UserState): LoggedInUser => {
  return {
    id: user?.account_id || '',
    publicName: user?.name || '',
    avatar: user?.picture || '',
    email: user?.email || '',
  };
};
